import cx from 'classnames';
import { ComponentProps } from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import {
    ItineraryCard,
    ItineraryCardProps,
} from '../../../design-system/components/card/itinerary/ItineraryCard';
import { Button } from '../../../design-system/components/form/button/Button';
import { Typography } from '../../../design-system/components/typography/Typography';
import { useBreakPoint } from '../../../domains/Layout/BreakPoint/BreakPoint';
import { Carousel, useCarousel } from '../../../domains/Layout/Carousel/Carousel';
import { ABTripDesignerCard } from '../../../partials/itinerary-list/partials/ABTripDesignerCard/ABTripDesignerCard';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { IntrinsicElement } from '../../utilities/types/IntrinsicElement';
import { PickDomain } from '../../utilities/types/PickDomain';

import style from './ItineraryListCarouselSection.module.css';

const DEFAULT_TRACKING_BLOCK = 'itineraries' as const;

type ItineraryCardTrackingInformation = Extract<
    ItineraryCardProps,
    { type?: 'default' }
>['trackingInformation'];

const TRIP_DESIGNER_POSITION = 4;

export function ItineraryListCarouselSection({
    destination,
    itineraries,
    destinationItinerariesCount,
    title: titleOverride,
    itineraryCardTitleElement = 'h3',
    target,
    trackingInformation,
    displayABTripDesignerCard,
}: {
    destination: PickDomain<Destination, 'id' | 'name' | 'preposition' | 'slug'>;
    destinationItinerariesCount: number;
    itineraries: Array<ItineraryCardProps['itinerary']>;
    title?: Partial<ComponentProps<typeof Typography>>;
    itineraryCardTitleElement?: IntrinsicElement;
    target?: ComponentProps<typeof ItineraryCard>['target'];
    trackingInformation?: {
        itineraryCard?: {
            click?: Pick<ItineraryCardTrackingInformation['click'], 'block'>;
            wishListAdded?: Pick<ItineraryCardTrackingInformation['wishListAdded'], 'block'>;
        };
        carousel?: {
            navClick?: Pick<TrackingProperties<typeof ampli.carouselClicked>, 'block'>;
        };
        itinerariesListCta?: Pick<TrackingProperties<typeof ampli.itinerariesListClicked>, 'block'>;
    };
    displayABTripDesignerCard?: {
        destination: PickDomain<Destination, 'id' | 'slug'>;
        utmCampaign?: 'page-list-itinerary' | 'page-itinerary';
    } | null;
}) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();
    const carousel = useCarousel();
    const { isDesktop } = useBreakPoint();

    const title = {
        as: 'div' as const,
        scale: 'primary-l-extra-bold' as const,
        children: formatMessage(
            { id: 'global.carrouselListIti.title' },
            {
                destinationPreposition: destination.preposition,
                Destination: destination.name,
            },
        ),
        ...titleOverride,
        className: cx(style.title, titleOverride?.className),
    };

    const showTripDesignerInCarousel = displayABTripDesignerCard && isDesktop();
    const showTripDesignerMobile = displayABTripDesignerCard && !isDesktop();

    const tripDesignerInsertPosition = TRIP_DESIGNER_POSITION - 1;

    return (
        <>
            <Typography {...title} />

            <Carousel
                {...carousel}
                className={style.carousel}
                slidesSize="large"
                slidesVisibleRules={{
                    sm: 2,
                    md: 2,
                    lg: 3,
                    xl: 3,
                    xxl: 4,
                }}
                trackingInformation={{
                    navClick: {
                        block:
                            trackingInformation?.carousel?.navClick?.block ??
                            DEFAULT_TRACKING_BLOCK,
                    },
                }}
            >
                {itineraries.flatMap((itinerary, index) => {
                    const cards = [];

                    if (showTripDesignerInCarousel && index === tripDesignerInsertPosition) {
                        cards.push(
                            <ABTripDesignerCard
                                key="trip-designer-card"
                                destination={displayABTripDesignerCard.destination}
                                utmCampaign={displayABTripDesignerCard.utmCampaign}
                            />,
                        );
                    }

                    const cardNumber =
                        showTripDesignerInCarousel && index >= tripDesignerInsertPosition
                            ? index + 2
                            : index + 1;

                    cards.push(
                        <ItineraryCard
                            key={itinerary.id}
                            target={target}
                            itinerary={itinerary}
                            titleElement={itineraryCardTitleElement}
                            image={{
                                sizes: '300px',
                            }}
                            trackingInformation={{
                                click: {
                                    block:
                                        trackingInformation?.itineraryCard?.click?.block ??
                                        DEFAULT_TRACKING_BLOCK,
                                    card_number: cardNumber,
                                    row_number: 0,
                                    tool_counter: 0,
                                },
                                wishListAdded: {
                                    block:
                                        trackingInformation?.itineraryCard?.wishListAdded?.block ??
                                        DEFAULT_TRACKING_BLOCK,
                                },
                            }}
                        />,
                    );

                    return cards;
                })}
            </Carousel>

            <div className={style.ctaContainer}>
                <Button
                    priority="secondary"
                    href={generatePath('itinerary_list', {
                        destinationSlug: destination.slug,
                    })}
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.itinerariesListClicked({
                                ...defaultProperties,
                                block:
                                    trackingInformation?.itinerariesListCta?.block ??
                                    DEFAULT_TRACKING_BLOCK,
                                destination_id: destination.id,
                                destination_slug: destination.slug,
                            });
                        });
                    }}
                >
                    {formatMessage(
                        { id: 'global.cta.listItineraries' },
                        {
                            destinationName: destination.name,
                            destinationPreposition: destination.preposition,
                            itineraryCount: destinationItinerariesCount,
                        },
                    )}
                </Button>
            </div>

            {showTripDesignerMobile ? (
                <div className={style.tripDesignerCardMobile}>
                    <ABTripDesignerCard
                        destination={displayABTripDesignerCard.destination}
                        utmCampaign={displayABTripDesignerCard.utmCampaign}
                    />
                </div>
            ) : null}
        </>
    );
}
